import { create } from 'zustand';
import {
  applyPrimaryShadesToElement,
  applyAccentColorToElement,
  clearAccentColorsFromElement,
  toHex,
} from '../lib/color';

/**
 * Branding state — ports `BrandingService` from the Angular member portal.
 *
 * Two layers, applied bottom-up:
 *   - `baseBranding`  — vertical/hub default (resolved during cold start
 *                       before tenant context is known). Used as fallback
 *                       when a child tenant omits logos / colors.
 *   - active branding — pushed via setBranding() once a tenant is resolved.
 *                       Overlays base branding; missing fields fall through.
 *
 * On every change we mutate `:root` CSS variables, the favicon, and the
 * `theme-color` meta. Branding is also persisted to localStorage so the
 * next cold start can paint cached branding before the API call returns.
 */

export interface BrandingConfig {
  domain: string;
  name: string;
  /** Used as `branding_key` API header for tenant resolution downstream. */
  brandingKey: string;
  /** Display product name (longer than `name` in some tenants). */
  productName: string;
  logoUrl: string;
  logoSmallUrl: string;
  primaryColor?: string;
  /** Brand accent — drives the brass/mustard accent token. */
  secondaryColor?: string;
  /** Secondary brand accent — drives the tertiary accent token. */
  tertiaryColor?: string;
  faviconUrl?: string;
  logoLargeUrl?: string;
}

/** Shape returned by Core/Tenant API `/resolve-domain` and `/branding`. */
export interface ApiBrandingData {
  name?: string;
  product_name?: string;
  branding_key?: string;
  logo_large_url?: string;
  logo_small_url?: string;
  favicon_url?: string;
  primary_color?: string;
  secondary_color?: string;
  tertiary_color?: string;
}

const STORAGE_KEY = 'paas_branding';
/**
 * The hub/base branding is persisted separately so it survives reloads.
 * Without this `baseBranding` is in-memory only and is lost on refresh —
 * a child community then has nothing to overlay and its missing logos/
 * colors fall back to hard defaults instead of the SSO hub's branding.
 */
const BASE_STORAGE_KEY = 'paas_base_branding';
const DEFAULT_LOGO = '/assets/logos/default/logo.svg';
const DEFAULT_LOGO_SMALL = '/assets/logos/default/logo-small.svg';
const DEFAULT_FAVICON = '/assets/logos/default/favicon.ico';
const FALLBACK_FAVICON = '/favicon.ico';
/** Fancourt forest green — matches the `--primary-*` token default. */
const DEFAULT_PRIMARY_COLOR = '#3a5a40';
const DEFAULT_BRANDING_KEY = 'PaaS';
const DEFAULT_NAME = 'IP4Estates';
const DEFAULT_PRODUCT_NAME = 'IP4Estates';

interface BrandingState {
  branding: BrandingConfig | null;
  baseBranding: BrandingConfig | null;

  /** Hub / vertical default. Populated during domain resolution before tenant overlay. */
  setBaseBrandingFromResolution: (
    domain: string,
    name?: string,
    primaryColor?: string,
    apiBranding?: ApiBrandingData,
  ) => void;

  /** Set tenant-active branding, falling back to baseBranding for missing fields. */
  setBranding: (
    domain: string,
    name?: string,
    primaryColor?: string,
    apiBranding?: ApiBrandingData,
  ) => void;

  /** Apply default placeholder branding while waiting for API. */
  setDefaultBranding: (domain: string) => void;

  /**
   * Full branding wipe (login storage reset, tests). Routine sign-out should
   * NOT call this — member portal keeps domain branding on the same hostname.
   */
  clearBranding: () => void;

  /**
   * Re-apply the hub/base branding as the active branding. Used when a
   * resident backs out of a community selection so the screen stops
   * showing the half-selected community's colors.
   */
  revertToBaseBranding: () => void;

  getBrandingKey: () => string;
  getProductName: () => string;
  getLogoUrl: () => string;
  getLogoSmallUrl: () => string;
  getOrganizationName: () => string;

  /** Hydrate from localStorage on first import. */
  _hydrate: () => void;
}

function buildBranding(
  domain: string,
  name: string | undefined,
  primaryColor: string | undefined,
  apiBranding: ApiBrandingData | undefined,
  base: BrandingConfig | null,
): BrandingConfig {
  const finalName = apiBranding?.name || name || base?.name || DEFAULT_NAME;
  const finalProductName =
    apiBranding?.product_name || finalName || base?.productName || DEFAULT_PRODUCT_NAME;
  return {
    domain,
    name: finalName,
    brandingKey: apiBranding?.branding_key || base?.brandingKey || DEFAULT_BRANDING_KEY,
    productName: finalProductName,
    logoUrl: apiBranding?.logo_large_url || base?.logoUrl || DEFAULT_LOGO,
    logoSmallUrl:
      apiBranding?.logo_small_url ||
      apiBranding?.logo_large_url ||
      base?.logoSmallUrl ||
      DEFAULT_LOGO_SMALL,
    primaryColor: apiBranding?.primary_color || primaryColor || base?.primaryColor || DEFAULT_PRIMARY_COLOR,
    secondaryColor: apiBranding?.secondary_color || base?.secondaryColor,
    tertiaryColor: apiBranding?.tertiary_color || base?.tertiaryColor,
    faviconUrl: apiBranding?.favicon_url || base?.faviconUrl || DEFAULT_FAVICON,
    logoLargeUrl: apiBranding?.logo_large_url || base?.logoLargeUrl,
  };
}

function applySideEffects(branding: BrandingConfig): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (branding.primaryColor) {
    applyPrimaryShadesToElement(root, branding.primaryColor);
  }
  // Brand accents — secondary drives the brass/mustard, tertiary the
  // tertiary accent. Absent values keep the tokens.css defaults.
  if (branding.secondaryColor) {
    applyAccentColorToElement(root, '--accent-brass', '--accent-brass-ink', branding.secondaryColor);
  }
  if (branding.tertiaryColor) {
    applyAccentColorToElement(root, '--accent-tertiary', '--accent-tertiary-ink', branding.tertiaryColor);
  }
  applyFavicon(branding.faviconUrl);
  applyThemeColor(branding.primaryColor || DEFAULT_PRIMARY_COLOR);
}

function applyFavicon(faviconUrl: string | undefined): void {
  if (!faviconUrl) return;
  const img = new Image();
  img.onload = () => setFavicons(faviconUrl);
  img.onerror = () => setFavicons(faviconUrl === DEFAULT_FAVICON ? FALLBACK_FAVICON : DEFAULT_FAVICON);
  img.src = faviconUrl;
}

function setFavicons(href: string): void {
  const head = document.head;
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    head.appendChild(link);
  }
  link.href = href;

  let appleTouch = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement | null;
  if (!appleTouch) {
    appleTouch = document.createElement('link');
    appleTouch.rel = 'apple-touch-icon';
    head.appendChild(appleTouch);
  }
  appleTouch.href = href;
}

function applyThemeColor(color: string): void {
  const hex = toHex(color) ?? DEFAULT_PRIMARY_COLOR;
  const themeMeta = document.querySelector("meta[name='theme-color']") as HTMLMetaElement | null;
  if (themeMeta) themeMeta.content = hex;
  const appleStatusMeta = document.querySelector(
    "meta[name='apple-mobile-web-app-status-bar-style']",
  ) as HTMLMetaElement | null;
  if (appleStatusMeta) appleStatusMeta.content = 'black-translucent';
}

function persist(branding: BrandingConfig, key: string = STORAGE_KEY): void {
  try {
    localStorage.setItem(key, JSON.stringify(branding));
  } catch (error) {
    console.warn('[branding] Failed to persist:', error);
  }
}

export const useBrandingStore = create<BrandingState>((set, get) => ({
  branding: null,
  baseBranding: null,

  setBaseBrandingFromResolution(domain, name, primaryColor, apiBranding) {
    const next = buildBranding(domain, name, primaryColor, apiBranding, null);
    set({ baseBranding: next });
    // Persist so the hub base survives reloads and stays the underlay for
    // any community the resident later switches into.
    persist(next, BASE_STORAGE_KEY);
  },

  setBranding(domain, name, primaryColor, apiBranding) {
    const { baseBranding } = get();
    const next = buildBranding(domain, name, primaryColor, apiBranding, baseBranding);
    set({ branding: next });
    persist(next);
    applySideEffects(next);
  },

  setDefaultBranding(domain) {
    get().setBranding(domain, DEFAULT_NAME, DEFAULT_PRIMARY_COLOR);
  },

  revertToBaseBranding() {
    const { baseBranding } = get();
    if (!baseBranding) return;
    set({ branding: baseBranding });
    persist(baseBranding);
    applySideEffects(baseBranding);
  },

  clearBranding() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(BASE_STORAGE_KEY);
    } catch { /* noop */ }
    set({ branding: null, baseBranding: null });
    if (typeof document !== 'undefined') {
      // Reset palette to defaults (re-evaluated from tokens.css on next paint).
      const root = document.documentElement;
      for (const k of ['--primary-50','--primary-100','--primary-200','--primary-300','--primary-400','--primary-500','--primary-600','--primary-700','--primary-800','--primary-900','--primary-ink']) {
        root.style.removeProperty(k);
      }
      clearAccentColorsFromElement(root);
    }
  },

  getBrandingKey() {
    return get().branding?.brandingKey ?? DEFAULT_BRANDING_KEY;
  },
  getProductName() {
    return get().branding?.productName ?? DEFAULT_PRODUCT_NAME;
  },
  getLogoUrl() {
    return get().branding?.logoUrl ?? DEFAULT_LOGO;
  },
  getLogoSmallUrl() {
    return get().branding?.logoSmallUrl ?? DEFAULT_LOGO_SMALL;
  },
  getOrganizationName() {
    return get().branding?.name ?? DEFAULT_NAME;
  },

  _hydrate() {
    if (typeof window === 'undefined') return;
    try {
      // Restore the hub/base underlay first so the active branding (and any
      // later community switch) overlays it rather than falling to defaults.
      const rawBase = localStorage.getItem(BASE_STORAGE_KEY);
      if (rawBase) {
        set({ baseBranding: JSON.parse(rawBase) as BrandingConfig });
      }
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const branding = JSON.parse(raw) as BrandingConfig;
      set({ branding });
      applySideEffects(branding);
    } catch (error) {
      console.warn('[branding] Failed to hydrate from storage:', error);
    }
  },
}));

/** Hydrate eagerly so cached branding paints before React renders. */
useBrandingStore.getState()._hydrate();
